import apiClient from "@/services/api/apiClient"
import { convertKeysToSnakeCase } from "@/services/adapters/caseConversion/caseConversions"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CompanyInfoField {
  name: string
  type: "single-line" | "multi-line" | "number" | "phone" | "email"
}

export interface CompanyInfoSection {
  section_name: string
  id: string
  fields: CompanyInfoField[]
  template_address_form?: CompanyInfoField[]
}

export type CompanyInfoData = Record<string, string>

export type CompanyProfileState =
  | "no_documents"
  | "pending"
  | "extracting"
  | "ready"
  | "confirmed"

export interface CompanyProfileStatus {
  state: CompanyProfileState
  hasProfile: boolean
  isConfirmed: boolean
  documentsCount: number
}

export interface OrganizationProfile {
  id: string
  organizationId: string
  profileData: CompanyInfoData
  extractionSource: string
  extractedAt: string
  status: "extracting" | "ready" | "confirmed"
  isConfirmed: boolean
  confirmedAt: string | null
  confirmedBy: string | null
  createdAt: string
  updatedAt: string
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

async function fetchCompanyProfileStatus(): Promise<CompanyProfileStatus> {
  const response = await apiClient.get<CompanyProfileStatus>(
    "/company-profile/status",
  )
  return response.data
}

async function ensureCompanyProfile(): Promise<CompanyProfileStatus> {
  const response = await apiClient.post<CompanyProfileStatus>(
    "/company-profile/ensure",
  )
  return response.data
}

async function fetchCompanyProfile(): Promise<OrganizationProfile> {
  const response = await apiClient.get<OrganizationProfile>("/company-profile")
  const profile = response.data
  // The response interceptor recursively camelCases all keys, including the
  // nested profile_data dict whose keys are domain field names (snake_case).
  // Restore them so template lookups like formData["company_name"] work.
  if (profile.profileData) {
    profile.profileData = convertKeysToSnakeCase(profile.profileData)
  }
  return profile
}

async function saveCompanyProfile(
  data: CompanyInfoData,
): Promise<OrganizationProfile> {
  const response = await apiClient.put<OrganizationProfile>(
    "/company-profile",
    { profile_data: data },
  )
  return response.data
}

async function confirmCompanyProfile(): Promise<OrganizationProfile> {
  const response = await apiClient.post<OrganizationProfile>(
    "/company-profile/confirm",
  )
  return response.data
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const COMPANY_PROFILE_QUERY_KEYS = {
  status: ["company-profile", "status"] as const,
  profile: ["company-profile"] as const,
}

// ---------------------------------------------------------------------------
// React Query hooks
// ---------------------------------------------------------------------------

export function useGetCompanyProfileStatus({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: COMPANY_PROFILE_QUERY_KEYS.status,
    queryFn: fetchCompanyProfileStatus,
    enabled,
    // Poll every 3 s while extraction is pending or running so consumers
    // (sidebar dot, CompanyInfoPage) stay in sync without a manual refresh.
    refetchInterval: (query) => {
      const state = query.state.data?.state
      return state === "pending" || state === "extracting" ? 3000 : false
    },
  })
}

export function useEnsureCompanyProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ensureCompanyProfile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: COMPANY_PROFILE_QUERY_KEYS.status,
      })
      await queryClient.invalidateQueries({
        queryKey: COMPANY_PROFILE_QUERY_KEYS.profile,
      })
    },
  })
}

export function useGetCompanyProfile() {
  return useQuery({
    queryKey: COMPANY_PROFILE_QUERY_KEYS.profile,
    queryFn: fetchCompanyProfile,
    // Poll every 3 s while extraction is running so the page transitions
    // automatically when the background task completes.
    refetchInterval: (query) =>
      query.state.data?.status === "extracting" ? 3000 : false,
  })
}

export function useSaveCompanyInfo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: saveCompanyProfile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: COMPANY_PROFILE_QUERY_KEYS.profile,
      })
      await queryClient.invalidateQueries({
        queryKey: COMPANY_PROFILE_QUERY_KEYS.status,
      })
    },
  })
}

export function useConfirmCompanyProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: confirmCompanyProfile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: COMPANY_PROFILE_QUERY_KEYS.profile,
      })
      await queryClient.invalidateQueries({
        queryKey: COMPANY_PROFILE_QUERY_KEYS.status,
      })
    },
  })
}
